import React, { useState } from "react";
import axios from "axios";
import styles from "./userDashboardHome.module.css";
import toast from "react-hot-toast";
import PaymentModals from "../../pages/models/PaymentModals";
import { render } from "@testing-library/react";

const TableSection = ({ title, columns, data, onRowClick, isClickable }) => {
  return (
    <div className={styles.section}>
      <h3>{title}</h3>
      <div className={styles.tableWrapper}>
        <table>
          <thead>
            <tr>
              {columns.map((col, i) => (
                <th key={i}>{col?.header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, idx) => (
              <tr
                key={idx}
                onClick={isClickable ? () => onRowClick(row) : undefined}
                className={isClickable ? styles.clickableRow : ""}
              >
                {columns.map((col, i) => (
                  <td key={i}>{col.render(row)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const UserDashboardHome = () => {
  const [phone, setPhone] = useState("");
  const [userDetails, setUserDetails] = useState([]);
  const [paymentDetails, setPaymentDetails] = useState([]);
  const [subscriptionDetails, setSubscriptionDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [editedPayment, setEditedPayment] = useState(null);
  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString();
  const storedUser = JSON.parse(localStorage.getItem("user"));
  const userType = storedUser?.userDetails?.user_type;
  const fetchData = async () => {
    if (!phone) {
      setError("Please enter a phone number");
      return;
    }

    setLoading(true);
    setError("");
    setUserDetails([]);
    setPaymentDetails([]);
    setSubscriptionDetails([]);
    setSelectedPayment(null);
    try {
      const response = await axios.get(
        `/tnibroadmin/user-plan-details?phoneno=${phone}`
      );
      // if (response.data.results.status === 'success') {
      const { user_details, payment_details, user_subscription } =
        response.data.result;
      setUserDetails(user_details);
      setPaymentDetails(payment_details);
      setSubscriptionDetails(user_subscription);
      // }
      //  else {
      //   setError('No data found');
      // }
    } catch (err) {
      setError(err.response.data.response);
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = async (row) => {
    try {
      const response = await axios.get(`/payment/payment-detail?id=${row.id}`);
      setSelectedPayment(response.data?.response[0]);
      setEditedPayment(response.data?.response[0]);
      setOpenModal(true);
    } catch (err) {
      alert("Error fetching payment detail");
    }
  };
  const handlePostPayment = async () => {
    if (!selectedPayment) return;
    try {
      await axios.post("/payment/payment-detail", editedPayment);
      toast.success("Payment details updated");
      setOpenModal(false);
      await fetchData();
    } catch (err) {
      alert("Failed to post payment details");
      toast.error("Failed to post payment details");
    }
  };

  const handleCreateSubscription = async (paymentId, userId) => {
    try {
      const payload = {
        id: paymentId,
        userid: userId,
      };

      const res = await axios.post(
        "/payment/create-subscription-by-payment",
        payload
      );
      toast.success("Subscription created successfully");
      await fetchData();
    } catch (err) {
      const msg = err?.response?.data?.response || "Something went wrong";
      toast.error(msg);
    }
  };

  return (
    <div className={styles.container}>
      <h2>Search User by Phone</h2>
      <div className={styles.inputGroup}>
        <input
          type="text"
          placeholder="Enter Phone Number"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className={styles.input}
        />
        <button onClick={fetchData} className={styles.button}>
          Search
        </button>
      </div>

      {loading && <p>Loading...</p>}
      {error && <p className={styles.error}>{error}</p>}

      {userDetails?.length > 0 && (
        <TableSection
          title="User Info"
          data={userDetails}
          columns={[
            { header: "Phone", render: (u) => u.phone_number },
            { header: "Name", render: (u) => u.name },
            { header: "User Type", render: (u) => u.user_type },
            { header: "Status", render: (u) => u.status },
            { header: "Date Joined", render: (u) => formatDate(u.date_joined) },
            { header: "Referral Code", render: (u) => u.referralcode || "N/A" },
          ]}
        />
      )}

      {paymentDetails?.length > 0 && (
        <TableSection
          title="Payment History"
          data={paymentDetails}
          onRowClick={handleRowClick}
          isClickable={userType === "Admin"}
          columns={[
            { header: "Payment ID", render: (p) => p.id || "N/A" },
            { header: "Plan Name", render: (p) => p.plan || "N/A" },
            { header: "Plan ID", render: (p) => p.plan_id || "N/A" },
            { header: "Order ID", render: (p) => p.provider_order_id || "N/A" },
            {
              header: "Razorpay Payment ID",
              render: (p) => p.razorpay_payment_id || "N/A",
            },
            {
              header: "Status",
              render: (p) => {
                const status = String(p.payment_status).toLowerCase();
                return status === "success" || status === "true" ? (
                  <span style={{ color: "green", fontWeight: "bold" }}>
                    Success
                  </span>
                ) : (
                  <span style={{ color: "red", fontWeight: "bold" }}>
                    Failed
                  </span>
                );
              },
            },
            { header: "Amount", render: (p) => p.amount },
            { header: "Date", render: (p) => formatDate(p.payment_date) },
            { header: "Total Alerts", render: (p) => p.total_alerts },
            { header: "Used Alerts", render: (p) => p.used_alert },
            ...(userType === "Admin"
              ? [
                  {
                    header: "Actions",
                    render: (p) =>
                      p.payment_status === true && (
                        <button
                          className={styles.button}
                          onClick={(e) => {
                            e.stopPropagation();
                            const userId = userDetails[0]?.id;
                            if (userId) {
                              handleCreateSubscription(p.id, userId);
                            } else {
                              toast.error("User ID not found");
                            }
                          }}
                        >
                          Make Subscription
                        </button>
                      ),
                  },
                ]
              : []),
          ]}
        />
      )}
      {selectedPayment && (
        <PaymentModals
          open={openModal}
          onClose={() => setOpenModal(false)}
          editedPayment={editedPayment}
          setEditedPayment={setEditedPayment}
          handlePostPayment={handlePostPayment}
        />
      )}
      {subscriptionDetails?.length > 0 && (
        <TableSection
          title="Subscription Info"
          data={subscriptionDetails}
          columns={[
            { header: "Plan Name", render: (s) => s.plan_name },
            { header: "Subscription ID", render: (s) => s.subscription_id },
            { header: "Start Date", render: (s) => formatDate(s.start_date) },
            {
              header: "Next Billing",
              render: (s) => formatDate(s.next_billing_date),
            },
            { header: "Credits", render: (s) => s.credits },
            { header: "Auto Pay", render: (s) => (s.autopay ? "Yes" : "No") },
            {
              header: "Status",
              render: (s) => (s.active ? "Active" : "Inactive"),
            },
          ]}
        />
      )}
    </div>
  );
};

export default UserDashboardHome;
